import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
} from '@mui/material';
import { Notification } from '@progress/kendo-react-notification';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import { authService } from '../../..';
import WebhookConfigPanel from './WebhookConfigPanel';
import { httpGetAsync, httpPostAsync } from '../../../utils/HttpClient';

// Types
import {
  SecureCloseConfigurationStatus,
  SecureCloseCompany,
  SecureCloseFlow,
} from '../../../Interfaces/ESign/secureCloseTypes';

enum ESignSignerRole {
  Buyer = 1,
  CoBuyer = 2,
  Dealer = 3,
  Employee = 4,
  Lender = 5,
  CoBuyer2 = 6,
  ThirdPartyOwner = 7
}

const ESignSignerRoleOptions = [
  { value: ESignSignerRole.Buyer, label: "Buyer" },
  { value: ESignSignerRole.CoBuyer, label: "Co-Buyer" },
  { value: ESignSignerRole.Dealer, label: "Dealer" },
  { value: ESignSignerRole.Employee, label: "Employee" },
  { value: ESignSignerRole.Lender, label: "Lender" },
  { value: ESignSignerRole.CoBuyer2, label: "Co-Buyer 2" },
  { value: ESignSignerRole.ThirdPartyOwner, label: "Third Party Owner" }
];

interface SignerInput {
  role: ESignSignerRole;
  name: string;
  email: string;
}

interface DocumentOption {
  id: string;
  name: string;
}

export interface SecureCloseDialogProps {
  applicationId: string;
  applicationNumber?: string;
  documentIds: string[];
  documentNames?: { id: string; name: string }[];
  buyerName?: string;
  buyerEmail?: string;
  coBuyerName?: string;
  coBuyerEmail?: string;
  employeeName?: string;
  employeeEmail?: string;
  onClose: () => void;
  onSuccess?: (requestId: string) => void;
}

const steps = ['Select Company, Flow & Documents', 'Add Signers', 'Review & Send'];

export default function SecureCloseDialog(props: SecureCloseDialogProps) {
  const {
    applicationId,
    applicationNumber,
    documentIds,
    documentNames,
    buyerName,
    buyerEmail,
    coBuyerName,
    coBuyerEmail,
    employeeName,
    employeeEmail,
    onClose,
    onSuccess,
  } = props;

  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Configuration state
  const [configStatus, setConfigStatus] = useState<SecureCloseConfigurationStatus | null>(null);

  // Company & Flow state
  const [companies, setCompanies] = useState<SecureCloseCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [flows, setFlows] = useState<SecureCloseFlow[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [loadingFlows, setLoadingFlows] = useState(false);

  // Document selection state
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([...documentIds]);

  // Template mapping state: documentId -> templateId
  const [documentTemplateMap, setDocumentTemplateMap] = useState<Record<string, string>>({});

  // Signer state
  const [signers, setSigners] = useState<SignerInput[]>([]);

  // Email details
  const [subject, setSubject] = useState(applicationNumber ? `#${applicationNumber} Documents` : 'Please sign these documents');
  const [message, setMessage] = useState('Please review and sign the attached documents at your earliest convenience.');

  // Success state
  const [signerLinks, setSignerLinks] = useState<{ name: string; url: string }[]>([]);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [successRequestId, setSuccessRequestId] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Webhook config view
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);

  // Load configuration and companies on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load flows when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadFlows(selectedCompanyId);
    } else {
      setFlows([]);
      setSelectedFlowId('');
    }
  }, [selectedCompanyId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const bearerToken = await authService.getToken();

      // Load configuration
      const configResponse = await httpGetAsync('api/secureClose/configuration', bearerToken!);
      if (configResponse?.ok) {
        const status: SecureCloseConfigurationStatus = await configResponse.json();
        setConfigStatus(status);

        if (!status.isEnabled || !status.isConfigured) {
          setLoading(false);
          return;
        }
      } else {
        setError('Failed to load SecureClose configuration');
        setLoading(false);
        return;
      }

      // Load companies
      const companiesResponse = await httpGetAsync(`api/secureClose/companies?applicationId=${applicationId}`, bearerToken!);
      if (companiesResponse?.ok) {
        const companyList: SecureCloseCompany[] = await companiesResponse.json();
        setCompanies(companyList);
        if (companyList.length > 0) {
          setSelectedCompanyId(companyList[0].id);
        }
      }

      // Initialize signers
      initializeSigners();
    } catch (err) {
      setError('Failed to load SecureClose configuration');
    } finally {
      setLoading(false);
    }
  };

  const getDocName = (docId: string): string => {
    return documentNames?.find(d => d.id === docId)?.name || docId;
  };

  const loadTemplateDefaults = async (flowId: string, availableTemplateIds: string[]) => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync(`api/secureClose/template-defaults/${encodeURIComponent(flowId)}`, bearerToken!);
      if (!response?.ok) return;
      const saved: Record<string, string> = await response.json();
      if (!saved || Object.keys(saved).length === 0) return;
      const templateIdSet = new Set(availableTemplateIds);
      const mapped: Record<string, string> = {};
      for (const docId of documentIds) {
        const name = getDocName(docId);
        if (saved[name] && templateIdSet.has(saved[name])) {
          mapped[docId] = saved[name];
        }
      }
      setDocumentTemplateMap(mapped);
    } catch { /* ignore errors */ }
  };

  const saveTemplateDefaults = async (flowId: string, updatedMap: Record<string, string>) => {
    try {
      const bearerToken = await authService.getToken();
      // Convert doc IDs to doc names for the API
      const toSave: Record<string, string> = {};
      for (const [docId, templateId] of Object.entries(updatedMap)) {
        toSave[getDocName(docId)] = templateId;
      }
      await httpPostAsync(`api/secureClose/template-defaults/${encodeURIComponent(flowId)}`, toSave, bearerToken!);
    } catch { /* ignore errors */ }
  };

  const loadFlows = async (companyId: string) => {
    setLoadingFlows(true);
    setSelectedFlowId('');
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync(`api/secureClose/companies/${companyId}/flows?applicationId=${applicationId}`, bearerToken!);
      if (response?.ok) {
        const flowList: SecureCloseFlow[] = await response.json();
        setFlows(flowList);
        if (flowList.length > 0) {
          const firstFlow = flowList[0];
          setSelectedFlowId(firstFlow.id);
          setDocumentTemplateMap({});
          if (firstFlow.documentTemplates?.length) {
            loadTemplateDefaults(firstFlow.id, firstFlow.documentTemplates.map(t => t.id));
          }
        }
      }
    } catch (err) {
      setError('Failed to load flows');
    } finally {
      setLoadingFlows(false);
    }
  };

  const initializeSigners = () => {
    const initialSigners: SignerInput[] = [];

    if (buyerName || buyerEmail) {
      initialSigners.push({
        role: ESignSignerRole.Buyer,
        name: buyerName || '',
        email: buyerEmail || '',
      });
    }

    if (coBuyerName || coBuyerEmail) {
      initialSigners.push({
        role: ESignSignerRole.CoBuyer,
        name: coBuyerName || '',
        email: coBuyerEmail || '',
      });
    }

    if (initialSigners.length === 0) {
      initialSigners.push({
        role: ESignSignerRole.Buyer,
        name: '',
        email: '',
      });
    }

    setSigners(initialSigners);
  };

  const handleDocumentToggle = (documentId: string) => {
    setSelectedDocumentIds(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleSignerChange = (index: number, field: keyof SignerInput, value: any) => {
    setSigners(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addSigner = (role: ESignSignerRole) => {
    let name = '';
    let email = '';
    if (role === ESignSignerRole.Employee && employeeName && employeeEmail) {
      name = employeeName;
      email = employeeEmail;
    }
    setSigners(prev => [...prev, { role, name, email }]);
  };

  const removeSigner = (index: number) => {
    setSigners(prev => prev.filter((_, i) => i !== index));
  };

  const canProceedFromStep = (step: number): boolean => {
    switch (step) {
      case 0: {
        if (selectedCompanyId === '' || selectedFlowId === '' || selectedManagerId === '' || selectedDocumentIds.length === 0) return false;
        const selectedFlow = flows.find(f => f.id === selectedFlowId);
        const templates = selectedFlow?.documentTemplates ?? [];
        if (templates.length > 0) {
          // Every selected document must have a template assigned
          return selectedDocumentIds.every(docId => !!documentTemplateMap[docId]);
        }
        return true;
      }
      case 1: // Signers
        return signers.length > 0 && signers.every(s => s.name.trim() && s.email.trim() && s.email.includes('@'));
      case 2: // Review
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);

    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        'api/secureClose/send',
        {
          applicationId,
          documentIds: selectedDocumentIds,
          documentTemplateMap,
          signers: signers.map(s => ({
            role: s.role,
            name: s.name,
            email: s.email,
            signingOrder: 1,
          })),
          companyId: selectedCompanyId,
          flowId: selectedFlowId,
          locationId: flows.find(f => f.id === selectedFlowId)?.locationId || '',
          managerId: selectedManagerId,
          subject,
          message,
        },
        bearerToken!
      );

      if (response?.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccessRequestId(result.requestId);
          setSignerLinks(result.signerLinks || []);
          setShowSuccessView(true);
        } else {
          setError(result.errorMessage || 'Failed to send documents');
        }
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to send documents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send documents');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = (url: string, index: number) => {
    navigator.clipboard.writeText(url);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCloseSuccess = () => {
    setShowSuccessView(false);
    if (successRequestId) {
      onSuccess?.(successRequestId);
    }
    onClose();
  };

  // --- Render helpers ---

  const getDocumentDisplayName = (docId: string): string => {
    const found = documentNames?.find(d => d.id === docId);
    return found?.name || docId;
  };

  const renderStepContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      );
    }

    if (!configStatus?.isEnabled) {
      return (
        <Notification type={{ style: 'warning', icon: true }}>
          SecureClose integration is not enabled for your organization. Please contact your administrator.
        </Notification>
      );
    }

    if (!configStatus?.isConfigured) {
      return (
        <Notification type={{ style: 'warning', icon: true }}>
          SecureClose is not fully configured. Please ensure the API URL and Bearer Token are set in administration settings.
        </Notification>
      );
    }

    switch (activeStep) {
      case 0:
        return renderCompanyFlowDocumentsStep();
      case 1:
        return renderSignersStep();
      case 2:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderCompanyFlowDocumentsStep = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Company & Flow
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Company</InputLabel>
            <Select
              value={selectedCompanyId}
              label="Company"
              onChange={(e) => {
                setSelectedCompanyId(e.target.value as string);
                setSelectedManagerId('');
              }}
            >
              {companies.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth size="small" disabled={!selectedCompanyId || loadingFlows}>
            <InputLabel>Flow</InputLabel>
            <Select
              value={selectedFlowId}
              label="Flow"
              onChange={(e) => {
                const flowId = e.target.value as string;
                setSelectedFlowId(flowId);
                setDocumentTemplateMap({});
                const flow = flows.find(f => f.id === flowId);
                if (flow?.documentTemplates?.length) {
                  loadTemplateDefaults(flowId, flow.documentTemplates.map(t => t.id));
                }
              }}
            >
              {flows.map(f => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingFlows && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth size="small" disabled={!selectedCompanyId}>
            <InputLabel>Manager</InputLabel>
            <Select
              value={selectedManagerId}
              label="Manager"
              onChange={(e) => setSelectedManagerId(e.target.value as string)}
            >
              {(companies.find(c => c.id === selectedCompanyId)?.managers ?? []).map(m => (
                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Documents
      </Typography>
      {(() => {
        const selectedFlow = flows.find(f => f.id === selectedFlowId);
        const templates = selectedFlow?.documentTemplates ?? [];
        return (
          <List dense>
            {documentIds.map(docId => (
              <ListItem key={docId} disablePadding>
                <ListItemButton onClick={() => handleDocumentToggle(docId)} dense sx={{ flexGrow: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedDocumentIds.includes(docId)}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText primary={getDocumentDisplayName(docId)} />
                </ListItemButton>
                {selectedDocumentIds.includes(docId) && templates.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 200, ml: 2 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={documentTemplateMap[docId] || ''}
                      label="Template"
                      onChange={(e) => {
                        const val = e.target.value as string;
                        const newMap = { ...documentTemplateMap };
                        if (val) {
                          newMap[docId] = val;
                        } else {
                          delete newMap[docId];
                        }
                        setDocumentTemplateMap(newMap);
                        if (selectedFlowId) {
                          saveTemplateDefaults(selectedFlowId, newMap);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {templates.map(t => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </ListItem>
            ))}
          </List>
        );
      })()}
      {documentIds.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No documents available. Please select documents in the print step first.
        </Typography>
      )}
    </Box>
  );

  const renderSignersStep = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Signers
      </Typography>
      {signers.map((signer, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={signer.role}
                  label="Role"
                  onChange={(e) => handleSignerChange(index, 'role', e.target.value)}
                >
                  {ESignSignerRoleOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3.5}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={signer.name}
                onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
              />
            </Grid>
            <Grid item xs={3.5}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={signer.email}
                onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                size="small"
                color="error"
                onClick={() => removeSigner(index)}
                disabled={signers.length <= 1}
              >
                Remove
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Add signer:
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {ESignSignerRoleOptions
            .filter(opt => !signers.some(s => s.role === opt.value))
            .map(opt => (
            <Button
              key={opt.value}
              size="small"
              variant="outlined"
              onClick={() => addSigner(opt.value)}
            >
              + {opt.label}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Review & Send
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">Company</Typography>
          <Typography variant="body1">{companies.find(c => c.id === selectedCompanyId)?.name || selectedCompanyId}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">Flow</Typography>
          <Typography variant="body1">{flows.find(f => f.id === selectedFlowId)?.name || selectedFlowId}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" color="text.secondary">Manager</Typography>
          <Typography variant="body1">{companies.find(c => c.id === selectedCompanyId)?.managers?.find(m => m.id === selectedManagerId)?.name || selectedManagerId}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" color="text.secondary" gutterBottom>Documents ({selectedDocumentIds.length})</Typography>
      <Box sx={{ mb: 2 }}>
        {selectedDocumentIds.map(docId => (
          <Typography key={docId} variant="body2">• {getDocumentDisplayName(docId)}</Typography>
        ))}
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>Signers ({signers.length})</Typography>
      <Box sx={{ mb: 3 }}>
        {signers.map((s, i) => (
          <Typography key={i} variant="body2">
            • {ESignSignerRoleOptions.find(o => o.value === s.role)?.label}: {s.name} ({s.email})
          </Typography>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      <TextField
        fullWidth
        size="small"
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={{ mb: 2 }}
      />
    </Box>
  );

  const renderSuccessView = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="success.main">
        Documents Sent Successfully!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sent to SecureClose dashboard to finish e-signing process.
      </Typography>
    </Box>
  );

  // Main render
  if (showSuccessView) {
    return (
      <Dialog open onClose={handleCloseSuccess} maxWidth="sm" fullWidth>
        <DialogTitle>SecureClose E-Sign</DialogTitle>
        <DialogContent>{renderSuccessView()}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccess}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>SecureClose E-Sign</span>
          {configStatus?.isEnabled && configStatus?.isConfigured && !showWebhookConfig && (
            <Tooltip title="Webhook Configuration">
              <IconButton size="small" onClick={() => setShowWebhookConfig(true)}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {showWebhookConfig ? (
          <WebhookConfigPanel onBack={() => setShowWebhookConfig(false)} />
        ) : (
          <>
            {configStatus?.isEnabled && configStatus?.isConfigured && (
              <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                {steps.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {error && (
              <Notification type={{ style: 'error', icon: true }} closable onClose={() => setError(null)}>
                {error}
              </Notification>
            )}

            {renderStepContent()}
          </>
        )}
      </DialogContent>
      {!showWebhookConfig && (
        <DialogActions>
          <Button onClick={onClose} disabled={sending}>Cancel</Button>
          {configStatus?.isEnabled && configStatus?.isConfigured && (
            <>
              <Button onClick={handleBack} disabled={activeStep === 0 || sending}>
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceedFromStep(activeStep)}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSend}
                  disabled={sending || !canProceedFromStep(activeStep)}
                >
                  {sending ? <CircularProgress size={20} /> : 'Send for Signing'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
